// 1_ShoppingCartFactoringCreated.jsx
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { getSubProductConditions } from 'lib/api';
import { fetchDecisionEnginesAvailables } from 'lib/api/decisionEngine';
import { createFactoringRequest, editParamsOnReview } from 'lib/api/factoring';
import {
  sendDraftToReview,
  submitFactoringQuoteQuotation,
} from 'lib/api/factoring/updateStatus';
import { getPayoutPlanByRequestId } from 'lib/api/payoutPlan';
import { getRequestExpensesCategories } from 'lib/api/requestExpenses';
import CustomAlert from 'lib/designSystem/Alert';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import {
  calculateRequestDetails,
  showAlert,
  validateCompanyTypes,
  validatePermissions,
} from 'lib/functions/utils';
import { PayoutDistribution, RequestExpenseCategory } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AuthState } from 'services/redux/authSlice';
import { decrementPendingRequests } from 'services/redux/notificactionSlice';

import DocumentsPreview from '../../../../../../../../../common/documentsPreview';
import Parameters from '../../../../../../../../../common/parameters';
import SimulationDoneLive from '../../../../../../../../../common/SimulationDoneLive';
import DialogActionsWrapper from '../DialogActionWrapper';

type Expense = {
  name: string;
  amount: number;
  hasIva: boolean;
};

const ShoppingCartFactoringCreated = (props: any) => {
  const {
    setOpenShoppingCart,
    selectedRequest,
    setSelectedRequest,
    setSuccessOpen,
    setFailureOpen,
    setUp,
    setLoading,
    executiveCreating,
    businessId,
    onlySimulation,
    inReview,
    setOpenDatesSelection,
    setBackwards,
    subProduct,
    setFormData,
    formData,
    requestConfiguration,
    resetRequestToOriginal,
    selectedId,
    availableArrearTables,
    executivesPortfolio,
    executivesAll,
    documentsToApply,
    setDocumentsToApply,
  } = props;
  const { id } = useParams<{ id: string }>();
  const [onEditing, setOnEditing] = useState(true);
  const [alertText, setAlertText] = useState('');
  const [showForm, setShowForm] = useState<any>({
    advance: true,
    rate: true,
    operationCost: true,
  });
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [operationDetails, setOperationDetails] = useState<any>({
    operationAmount: null,
    operationAdvanced: null,
    guaranteeAmount: null,
    operationCostRate: null,
    operationCostComissionFixed: null,
    operationCostComissionVariable: null,
    expensesCost: null,
    operationCostIva: null,
    operationAmountToApply: null,
    operationToDepost: null,
    advance: null,
    rate: null,
  });
  const dispatch = useDispatch();
  const [amountOverZero, setAmountOverZero] = useState(false);

  const [formErrors, setFormErrors] = useState<any>({});

  const [doneFirstSetup, setDoneFirstSetup] = useState(false);

  const startingDocumentSelected = selectedRequest.documents
    .filter((document: any) => document.active)
    .map((document: any) => document.id);

  const [selected, setSelected] = React.useState<readonly string[]>(
    startingDocumentSelected,
  );

  const [requestExpensesCategories, setRequestExpensesCategories] = useState<
    RequestExpenseCategory[]
  >([]);

  const [payoutPlan, setPayoutPlan] = useState<PayoutDistribution[]>([]);

  const [openDraftDialog, setOpenDraftDialog] = useState(false);
  const [createAsDraft, setCreateAsDraft] = useState(true);
  const [isException, setIsException] = useState(false);
  const [businessBlacklisted, setBusinessBlacklisted] = useState(false);
  const [stakeholdersBlacklisted, setStakeholdersBlacklisted] = useState<
    string[]
  >([]);

  // Decision engine after edit
  const [openPreEditDialog, setOpenPreEditDialog] = useState(false);
  const [canUseClientDecisionEngine, setCanUseClientDecisionEngine] =
    useState(false);
  const [canUsePayersDecisionEngine, setCanUsePayersDecisionEngine] =
    useState(false);
  const [runDecisionEngineRequest, setRunDecisionEngineRequest] =
    useState(false);
  const [runDecisionEnginePayers, setRunDecisionEnginePayers] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);

  const created = selectedRequest.status === 'Creada';

  const handleSubmitQuotation = async () => {
    if (!validateParams()) return alert('Por favor completa todos los campos');
    setLoading(true);
    try {
      await submitFactoringQuoteQuotation(dispatch, {
        advance: Number(formData.advance),
        guaranteeAmount: Number(formData.guaranteeAmount),
        rate: Number(formData.rate),
        fundRate: Number(formData.fundRate),
        arrearRate: Number(formData.arrearRate),
        amountToApply: formData.amountToApply,
        operationCostFixed: formData.operationCostComissionFixed,
        operationCostVariable: formData.operationCostComissionVariable,
        documentsToApply: documentsToApply.map((document: any) => ({
          id: document.id,
          amountToConciliate: document.amountToConciliate,
        })),
        expenses: formData.expenses.filter(
          (expense: Expense) => expense.amount > 0,
        ),
        operationCost: formData.operationCost,
        id: selectedRequest.quoteActive.id,
        status: 'QUOTED',
        documents: selectedRequest.documents,
        contactData: formData.contactData,
        onlySimulation,
        operationDate: formData.operationDate,
        guaranteeObservation: formData.guaranteeObservation,
        arrearType: formData.arrearType,
        arrearTableId: formData.arrearTableId,
      });
      dispatch(decrementPendingRequests());
      setSuccessOpen(
        `Se ha enviado la solicitud ${selectedRequest.id} al cliente`,
      );
      await setUp();
    } catch (error) {
      console.error(error);
      setFailureOpen('Lamentablemente ha ocurrido un error');
    }
    setLoading(false);
    setOpenShoppingCart(false);
  };

  const handlePreEdit = async () => {
    if (formData.arrearType === 'table' && !formData.arrearTableId) {
      setFormErrors({
        ...formErrors,
        arrearTableId: true,
      });
      return showAlert(
        dispatch,
        'warning',
        'Por favor selecciona una tabla de mora para el interés de mora',
      );
    }
    if (formData.guaranteeAmount > 0 && !formData.guaranteeObservation) {
      showAlert(
        dispatch,
        'warning',
        'Por favor agrega una observación al monto en garantía',
      );
      return setFormErrors({
        ...formErrors,
        guaranteeObservation: true,
      });
    }
    if (!validateParams()) return;
    if (selectedRequest.status === 'Borrador') {
      return handleEditOnInReview();
    }
    setOpenPreEditDialog(true);
    setDecisionLoading(true);
    try {
      const decisionEngines = await fetchDecisionEnginesAvailables(
        dispatch,
        'factoring-request',
      );
      const payersDecisionEngine = await fetchDecisionEnginesAvailables(
        dispatch,
        'factoring-request-payers',
      );
      let goToHandleEdit = true;
      for (const engine of decisionEngines) {
        if (engine.selected) {
          setCanUseClientDecisionEngine(true);
          goToHandleEdit = false;
        }
      }
      for (const engine of payersDecisionEngine) {
        if (engine.selected) {
          setCanUsePayersDecisionEngine(true);
          goToHandleEdit = false;
        }
      }
      if (goToHandleEdit) {
        setDecisionLoading(false);
        setOpenPreEditDialog(false);
        await handleEditOnInReview();
      }
    } catch (error) {
      setOpenPreEditDialog(false);
    }
    setDecisionLoading(false);
  };

  const handleEditOnInReview = async () => {
    setLoading(true);
    try {
      const filteredDocuments = selectedRequest.documents.filter(
        (document: any) => selected.includes(document.id),
      );
      await editParamsOnReview(dispatch, selectedRequest.id, {
        advance: Number(formData.advance),
        rate: Number(formData.rate),
        fundRate: Number(formData.fundRate),
        operationCostFixed: formData.operationCostComissionFixed,
        operationCostVariable: formData.operationCostComissionVariable,
        arrearRate: Number(formData.arrearRate),
        amountToApply: formData.amountToApply,
        expenses: formData.expenses.filter(
          (expense: Expense) => expense.amount > 0,
        ),
        documentsToApply: documentsToApply.map((document: any) => ({
          id: document.id,
          amountToConciliate: document.amountToConciliate,
        })),
        editedDocumentsToApply: formData.editedDocumentsToApply,
        documents: filteredDocuments,
        requestDate: selectedRequest.requestDate,
        subProductId: selectedRequest.subProduct,
        guaranteeAmount: formData.guaranteeAmount,
        guaranteeObservation: formData.guaranteeObservation,
        arrearType: formData.arrearType,
        arrearTableId: formData.arrearTableId,
        runDecisionEngineRequest,
        runDecisionEnginePayers,
        executive: formData.executive,
        documentsToUpdate: selectedRequest.documentsToUpdate,
        isDraft: selectedRequest.status === 'Borrador',
        payoutPlan: payoutPlan.map((p) => ({
          bankAccountId: p.bankAccount.id,
          amount: p.amount,
        })),
      });
      await setUp(selectedId);
      setOpenShoppingCart(false);
      setSuccessOpen(`Se ha actualizado la solicitud ${selectedRequest.id}.`);
    } catch (error) {
      setFailureOpen('Lamentablemente ha ocurrido un error');
    }
    setLoading(false);
  };

  const handleCreateQuote = async (isDraft = false) => {
    if (!validateParams()) return;
    setOpenDraftDialog(false);
    setLoading(true);
    try {
      const quotedData = {
        advance: Number(formData.advance),
        guaranteeAmount: Number(formData.guaranteeAmount),
        rate: Number(formData.rate),
        fundRate: Number(formData.fundRate),
        operationCostFixed: formData.operationCostComissionFixed,
        operationCostVariable: formData.operationCostComissionVariable,
        arrearRate: Number(formData.arrearRate),
        expenses: formData.expenses.filter(
          (expense: Expense) => expense.amount > 0,
        ),
        amountToApply: formData.amountToApply,
        guaranteeObservation: formData.guaranteeObservation,
        arrearType: formData.arrearType,
        arrearTableId: formData.arrearTableId,
        executive: formData.executive,
      };

      const filteredDocuments = selectedRequest.documents.filter(
        (document: any) => selected.includes(document.id),
      );

      const payload = {
        executives: [user?.id],
        requestsDocuments: filteredDocuments,
        businessIdentifier: id ?? businessId,
        documentsToApply: documentsToApply.map((document: any) => ({
          id: document.id,
          amountToConciliate: document.amountToConciliate,
        })),
        subProductId: selectedRequest.subProduct || 'electronic-invoice',
        quotedData,
        payoutPlan: payoutPlan.map((p) => ({
          bankAccountId: p.bankAccount.id,
          amount: p.amount,
        })),
        userEmail: formData.email,
        showForm,
        exception: isException,
        contactData: formData.contactData,
        useCase: onlySimulation ? 'quotation' : 'financing-hub',
        requestDate: selectedRequest.requestDate,
        executive: formData.executive,
        documentsToUpdate: selectedRequest.documentsToUpdate,
        isDraft,
      };

      const { factoringRequest } = await createFactoringRequest(
        dispatch,
        payload,
      );
      setSuccessOpen(`Se ha enviado la solicitud a evaluación.`);
      setOpenShoppingCart(false);
      if (businessId) {
        if (factoringRequest.id) {
          await setUp(factoringRequest.id);
        }
        await setUp();
      }
    } catch (error) {
      console.error(error);
      alert('Lamentablemente ha ocurrido un error');
    }
    setLoading(false);
  };

  const handleForward = async (isDraft = false) => {
    if (formData.arrearType === 'table' && !formData.arrearTableId) {
      setFormErrors({
        ...formErrors,
        arrearTableId: true,
      });
      return showAlert(
        dispatch,
        'warning',
        'Por favor selecciona una tabla de mora para el interés de mora',
      );
    }
    if (formData.guaranteeAmount > 0 && !formData.guaranteeObservation) {
      showAlert(
        dispatch,
        'warning',
        'Por favor agrega una observación al monto en garantía',
      );
      return setFormErrors({
        ...formErrors,
        guaranteeObservation: true,
      });
    }
    if (executiveCreating) {
      return await handleCreateQuote(isDraft);
    } else {
      return await handleSubmitQuotation();
    }
  };

  const handleSendDraftToReview = async () => {
    setLoading(true);
    try {
      const factoringRequest = await sendDraftToReview(dispatch, {
        id: selectedRequest.quoteActive.id,
      });
      setSuccessOpen(`Se ha enviado la solicitud a revisión.`);
      setOpenShoppingCart(false);
      if (factoringRequest.id) {
        await setUp(factoringRequest.id);
      }
      await setUp();
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    setLoading(false);
  };

  const checkValidEmail = (email: string) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  };

  const validateParams = () => {
    if (
      !operationDetails.operationAmount ||
      !operationDetails.operationAdvanced ||
      !formData.advance ||
      !formData.rate ||
      !formData.executive
    ) {
      alert('Por favor completa todos los campos');
      return false;
    }
    if (operationDetails.operationCostRate < 0) {
      alert('El costo del interés no puede ser negativo');
      return false;
    }

    if (onlySimulation && !selectedRequest.userOwner) {
      if (!formData.contactData) {
        alert('Por favor agregar un contacto para enviar la simulación');
        return false;
      }
      if (formData.contactData.id) {
        return true;
      }
      if (!checkValidEmail(formData.contactData.email)) {
        alert('Por favor agregar un email válido');
        return false;
      }
    }
    if (validateCompanyTypes(user, 'FinancingHub')) {
      if (!formData.arrearRate && formData.arrearType === 'lineal') {
        alert('Por favor completa la tasa de mora');
        return false;
      }
    }
    return true;
  };

  const updateSimulationValues = () => {
    const requestToEvaluate = selectedRequest;
    if (doneFirstSetup) {
      requestToEvaluate.advance = formData.advance;
      requestToEvaluate.guaranteeAmount = formData.guaranteeAmount;
      requestToEvaluate.guaranteeObservation = formData.guaranteeObservation;
      requestToEvaluate.rate = formData.rate;
      requestToEvaluate.operationDate = formData.operationDate;
      requestToEvaluate.operationCostComissionFixed =
        formData.operationCostComissionFixed;
      requestToEvaluate.operationCostComissionVariable =
        formData.operationCostComissionVariable;
      requestToEvaluate.arrearRate = formData.arrearRate;
      requestToEvaluate.amountToApply = formData.amountToApply;
      requestToEvaluate.expenses = formData.expenses;
      requestToEvaluate.arrearType = formData.arrearType;
      requestToEvaluate.arrearTableId = formData.arrearTableId;
      requestToEvaluate.executive = formData.executive;
    }
    const {
      operationAmount,
      operationAdvanced,
      operationCostRate,
      operationToDepost,
      operationIva,
      operationCostComissionFixed,
      operationCostComissionVariable,
      advance,
      rate,
      expensesCost,
      expensesIva,
      documentsWithDetails,
      operationAmountToApply,
      guaranteeAmount,
      guaranteeObservation,
      expensesDetails,
    } = calculateRequestDetails(
      requestToEvaluate,
      requestConfiguration.expensesHasIVA,
      requestConfiguration.factoringOperationsSystemFlag,
    );
    requestToEvaluate.documents = documentsWithDetails;
    setSelectedRequest(requestToEvaluate);

    setOperationDetails({
      operationAmount,
      operationAdvanced,
      operationCostRate,
      operationToDepost,
      operationIva,
      expensesCost,
      expensesIva,
      expensesDetails,
      operationAmountToApply,
      operationCostComissionFixed,
      operationCostComissionVariable,
      advance,
      rate,
      guaranteeAmount,
      guaranteeObservation,
      fundRate: formData.fundRate,
      spread:
        formData.rate - formData.fundRate > 0
          ? (formData.rate - formData.fundRate).toFixed(2)
          : 0,
    });
    setDoneFirstSetup(true);
  };

  const disabledBecasueOfDocuments = () => {
    const filteredDocuments = selectedRequest.documents.filter(
      (document: any) => selected.includes(document.id),
    );
    if (filteredDocuments.length === 0) return true;
    return false;
  };

  const haveToSetup = async () => {
    if (inReview) {
      const selectedDocumentsToApply =
        selectedRequest.quoteActive.documentsToApply.map(
          (doc: any) => doc.documentId,
        );
      const previousData = {
        advance: selectedRequest.quoteActive.advance,
        rate: selectedRequest.quoteActive.rate,
        arrearRate: selectedRequest.quoteActive.arrearRate,
        arrearType: selectedRequest.arrearType || 'lineal',
        arrearTableId: selectedRequest.arrearTableId || null,
        operationCostComissionFixed:
          selectedRequest.quoteActive.operationCostFixed,
        operationCostComissionVariable:
          selectedRequest.quoteActive.operationCostVariable,
        operationCostComissionVariablePercentage: (
          (selectedRequest.quoteActive.operationCostVariable /
            selectedRequest.quoteActive.amount) *
          100
        ).toFixed(2),
        expenses: selectedRequest.quoteActive.expenses,
        selectedDocumentsToApply,
        amountToApply: selectedRequest.quoteActive.amountToApply,
        contactData: selectedRequest.contactData,
        requestDate: selectedRequest.requestDate,
        guaranteeAmount: selectedRequest.guaranteeAmount,
        guaranteeObservation: selectedRequest.guaranteeObservation,
        executive: selectedRequest.executiveId,
        fundRate: selectedRequest.quoteActive.fundRate || 0,
        spread:
          selectedRequest.rate - (selectedRequest.quoteActive.fundRate || 0) ||
          0,
      };
      setOnEditing(false);
      setFormData(previousData);
      setDocumentsToApply(
        selectedRequest.quoteActive.documentsToApply.map((doc: any) => ({
          id: doc.documentId,
          amountToConciliate: doc.amountToConciliate,
        })),
      );
    }
    if (created) {
      let subProductConditions = null;
      try {
        subProductConditions = await getSubProductConditions(dispatch, {
          businessId: selectedRequest.businessIdentifier,
          subProductId: selectedRequest.subProduct,
        });
      } catch (error) {
        console.error('Error loading subProduct conditions:', error);
      }
      if (subProductConditions) {
        setFormData({
          ...formData,
          advance: subProductConditions.advance,
          rate: subProductConditions.rate
            ? subProductConditions.rate
            : (subProductConditions.spread || 0) +
              (subProductConditions.fundRate || 0),
          arrearRate: subProductConditions.arrearRate,
          operationCostComissionFixed: subProductConditions.operationCostFixed,
          operationCostComissionVariablePercentage:
            subProductConditions.operationCostVariable,
          operationCostComissionVariable:
            (subProductConditions.operationCostVariable / 100) *
            selectedRequest.amount,
          fundRate: subProductConditions.fundRate || 0,
          spread: subProductConditions.spread || 0,
        });
      }
    }
  };

  const fetchRequestExpensesCategories = async () => {
    try {
      const requestExpensesCategories =
        await getRequestExpensesCategories(dispatch);
      setRequestExpensesCategories(requestExpensesCategories);
    } catch (error) {
      console.error('Error fetching request expenses categories:', error);
    }
  };

  const fetchPayoutPlan = async () => {
    try {
      const payoutPlan = await getPayoutPlanByRequestId(
        dispatch,
        'factoring-request',
        selectedRequest.id.toString(),
      );
      setPayoutPlan(payoutPlan);
    } catch (error) {
      console.error('Error fetching payout plan:', error);
    }
  };

  const handleBackwards = () => {
    if (executiveCreating) {
      setOpenDatesSelection(true);
      setOpenShoppingCart(false);
      setBackwards(true);
      return;
    }
    setOpenShoppingCart(false);
  };

  useEffect(() => {
    void haveToSetup();
    void fetchRequestExpensesCategories();
    if (selectedRequest) void fetchPayoutPlan();
  }, []);

  useEffect(() => {
    updateSimulationValues();
  }, [formData, selectedRequest, requestConfiguration]);

  const canEditRequest = () => {
    if (
      selectedRequest.status === 'En revisión' &&
      requestConfiguration.draftEnabled &&
      !validatePermissions(user, 'financing_factoring_edit')
    ) {
      return false;
    }
    return true;
  };

  return (
    <div>
      <Grid container>
        <Grid item xs={6.5}>
          <Parameters
            formData={formData}
            setFormData={setFormData}
            selectedRequest={selectedRequest}
            setDocumentsToApply={setDocumentsToApply}
            onlySimulation={onlySimulation}
            subProduct={subProduct}
            onEditing={onEditing}
            operationDetails={operationDetails}
            documentsToApply={documentsToApply}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            availableArrearTables={availableArrearTables}
            executivesPortfolio={executivesPortfolio}
            executivesAll={executivesAll}
            requestConfiguration={requestConfiguration}
            requestExpensesCategories={requestExpensesCategories}
            isException={isException}
            setIsException={setIsException}
          />
        </Grid>
        <Grid item xs={5.5}>
          <div style={{ padding: '0px 12px' }}>
            <SimulationDoneLive
              selectedRequest={selectedRequest}
              operationDetails={operationDetails}
              documentsToApply={documentsToApply}
              requestConfiguration={requestConfiguration}
              onEditing={onEditing}
              setAmountOverZero={setAmountOverZero}
              formData={formData}
              setFormData={setFormData}
              businessBlacklisted={businessBlacklisted}
              stakeholdersBlacklisted={stakeholdersBlacklisted}
              setBusinessBlacklisted={setBusinessBlacklisted}
              setStakeholdersBlacklisted={setStakeholdersBlacklisted}
              payoutPlan={payoutPlan}
              setPayoutPlan={setPayoutPlan}
            />
          </div>
        </Grid>
      </Grid>
      <DocumentsPreview
        selectedRequest={selectedRequest}
        simulationInProgress
        onEditing={onEditing}
        setSelected={setSelected}
        setSelectedRequest={setSelectedRequest}
      />
      {inReview ? (
        <DialogActionsWrapper
          type="factoring"
          setUp={setUp}
          onClose={() => setOpenShoppingCart(false)}
          selectedRequest={selectedRequest}
          actions={
            <>
              {onEditing && (
                <CustomButton
                  onClick={() => {
                    resetRequestToOriginal();
                  }}
                  color="secondary"
                >
                  Cancelar
                </CustomButton>
              )}
              {onEditing ? (
                <CustomButton
                  onClick={() => handlePreEdit()}
                  color="warning"
                  disabled={disabledBecasueOfDocuments() || !amountOverZero}
                >
                  Guardar
                </CustomButton>
              ) : (
                <>
                  {selectedRequest.status === 'Borrador' && (
                    <CustomButton
                      onClick={() => void handleSendDraftToReview()}
                      color="success"
                    >
                      Enviar a revisión
                    </CustomButton>
                  )}
                  <CustomButton
                    onClick={() => {
                      setAlertText(
                        'Al guardar, todas las aprobaciones existentes se eliminarán. ',
                      );
                      setOnEditing(true);
                      updateSimulationValues();
                    }}
                    color="primary"
                    disabled={!canEditRequest()}
                  >
                    Editar
                  </CustomButton>
                </>
              )}
            </>
          }
          selectedId={selectedId}
        />
      ) : (
        <DialogActionsWrapper
          type="factoring"
          setUp={setUp}
          onClose={handleBackwards}
          selectedRequest={selectedRequest}
          actions={
            <>
              <CustomButton
                onClick={() =>
                  executiveCreating && requestConfiguration.draftEnabled
                    ? setOpenDraftDialog(true)
                    : handleForward(false)
                }
                color="primary"
                disabled={
                  !amountOverZero ||
                  (businessBlacklisted &&
                    requestConfiguration.blockBusinessInBlacklist) ||
                  (stakeholdersBlacklisted.length > 0 &&
                    requestConfiguration.blockStakeholderInBlacklist)
                }
              >
                {executiveCreating ? 'Crear' : 'Cotizar'}
              </CustomButton>
            </>
          }
          selectedId={selectedId}
        />
      )}
      <CustomAlert
        setOpen={setAlertText}
        open={alertText.length > 0}
        title="Cuidado"
        text={alertText}
        severity="warning"
      />
      <Dialog
        open={openPreEditDialog}
        onClose={() => setOpenPreEditDialog(false)}
      >
        <DialogTitle>Editar solicitud</DialogTitle>
        <DialogContent>
          {decisionLoading && (
            <div style={{ minHeight: '100px' }}>
              <CenteredCircularProgress type="dialog" />
            </div>
          )}
          {!decisionLoading && (
            <>
              <Typography variant="body1">
                Estás a punto de editar la solicitud {selectedRequest.id}. Si lo
                deseas, puedes ejecutar nuevamente el motor de decisión para la
                solicitud.
              </Typography>
              <FormControl>
                <FormControlLabel
                  control={<Checkbox />}
                  disabled={!canUseClientDecisionEngine}
                  label="Deseo ejecutar el motor de decisión para esta solicitud"
                  onChange={(e: any) =>
                    setRunDecisionEngineRequest(e.target.checked)
                  }
                />
                <FormControlLabel
                  control={<Checkbox />}
                  disabled={!canUsePayersDecisionEngine}
                  label="Deseo ejecutar el motor de decisión para los pagadores"
                  onChange={(e: any) =>
                    setRunDecisionEnginePayers(e.target.checked)
                  }
                />
              </FormControl>
            </>
          )}
        </DialogContent>
        {!decisionLoading && (
          <DialogActions>
            <CustomButton
              onClick={() => setOpenPreEditDialog(false)}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={() => handleEditOnInReview()}
              color="primary"
            >
              Continuar
            </CustomButton>
          </DialogActions>
        )}
      </Dialog>
      <Dialog
        open={openDraftDialog}
        onClose={() => setOpenDraftDialog(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="draft-dialog-title"
      >
        <DialogTitle id="draft-dialog-title">
          Crear solicitud de factoring
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            ¿Deseas crear la solicitud de factoring como borrador? No se
            ejecutarán los motores de decicisión hasta que la solicitud pase a
            estado &quot;En revisión&quot;.
          </Typography>
          <FormControl margin="dense">
            <FormControlLabel
              control={<Checkbox size="small" checked={createAsDraft} />}
              label="Crear como borrador"
              onChange={(e: any) => setCreateAsDraft(e.target.checked)}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenDraftDialog(false)}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleForward(createAsDraft)}
            color="primary"
            disabled={!amountOverZero}
          >
            Crear
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ShoppingCartFactoringCreated;
